import React, { useEffect, useState } from "react";
import TaskView from "../common/TaskView";
import { AcousticTask, AcousticTaskDefinition } from "../common/models";
import { Typography } from "@mui/material";
import { randomUUID } from "../../../utils";
import { useLocalStorage } from "../../../../services/LocalStorageProvider";
import SlideShow, { Slide } from "../common/SlideShow";
import { translateReactNode } from "../../../../utils/translateReactNode";
import { useTranslation } from "react-i18next";

const slides: Slide[] = [{
    imageSrc: "assets/images/StoryRecall/StoryRecall_1.jpg",
    caption: "Image 1",
    text: <>There was once a boy who had a dog and a pet frog. He kept the frog in a large jar in his bedroom.</>
  }, {
    imageSrc: "assets/images/StoryRecall/StoryRecall_2.jpg",
    caption: "Image 2",
    text: <>One night while he and his dog were sleeping, the frog climbed out of the jar. He jumped out of an open window.</>
  }, {
    imageSrc: "assets/images/StoryRecall/StoryRecall_3.jpg",
    caption: "Image 3",
    text: <>When the boy and the dog woke up the next morning, they saw that the jar was empty.</>
  }, {
    imageSrc: "assets/images/StoryRecall/StoryRecall_4.jpg",
    caption: "Image 4",
    text: <>The boy looked everywhere for the frog. The dog looked for the frog too. When the dog tried to look in the jar, he got his head stuck.</>
  }, {
    imageSrc: "assets/images/StoryRecall/StoryRecall_5.jpg",
    caption: "Image 5",
    text: <>The boy and the dog looked outside for the frog. The boy called out, “Frog, where are you?”</>
  }, {
    imageSrc: "assets/images/StoryRecall/StoryRecall_6.jpg",
    caption: "Image 6",
    text: <>The boy climbed up on a rock and called again for his frog. He held onto some branches so he wouldn't fall.</>
  }, {
    imageSrc: "assets/images/StoryRecall/StoryRecall_7.jpg",
    caption: "Image 7",
    text: <>But the branches weren't really branches! They were deer antlers. The deer picked the boy up on his head and started running. The deer stopped suddenly and the boy and the dog fell over the edge of a cliff.</>
  }, {
    imageSrc: "assets/images/StoryRecall/StoryRecall_8.jpg",
    caption: "Image 8",
    text: <>There was a pond below the cliff. They landed with a splash right on top of one another.</>
  }, {
    imageSrc: "assets/images/StoryRecall/StoryRecall_9.jpg",
    caption: "Image 9",
    text: <>They heard a familiar sound. They crept up and looked behind a big log.</>
  }, {
    imageSrc: "assets/images/StoryRecall/StoryRecall_10.jpg",
    caption: "Image 10",
    text: <>There they found the boy's pet frog, with a mother frog and other baby frogs. The end.</>
  }
];

const TUTORIAL_URLS: Record<string, string> = {
  'en': 'assets/videos/tutorials/Story_Recall_v2.mp4',
  'es-419': 'assets/videos/tutorials/Story_Recall_v2-es-419.mp4'
}

const StoryRecallTextDisplay = ({onLastSlideShown, cohort}: {onLastSlideShown: () => void, cohort: string }) => {
  const [dismissed, setDismissed] = React.useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = React.useState(0);
  const { storage } = useLocalStorage();
  const isRecording = storage?.isRecording || false;

  useEffect(() => {
    if (dismissed) {
      onLastSlideShown();
    }
  }, [dismissed]);

  useEffect(() => {
    if (isRecording && !dismissed) {
      setDismissed(true);
    }
  }, [isRecording])

  const onChange = (index: number) => {
    setCurrentSlideIndex(index);
    if (index === slides.length - 1) {
      onLastSlideShown();
    }
  }

  return (
    <>
      <div style={{
        margin: '1rem 0',
        padding: '1rem 0',
        background: '#e5e5e5',
        borderRadius: '5px'
        }}>
          <SlideShow slides={slides} index={currentSlideIndex} onChange={onChange} hideText={dismissed} cohort={cohort} />
        </div>
    </>
  );
};

const Definition: AcousticTaskDefinition = {
  name: "Story recall (v2)",
  cohort: 'generic',
  title: "Story recall",
  description: "",
  type: 'acoustic-task',
  estimatedCompletionTime: '3:25',
  subTaskDefinitions: [{
    name: "Story recall (v2)",
    title: "",
    instructions: <>
      <Typography variant="h5" component='div'>
        Below you will be presented with a story about a boy and a frog. You may click forward to see the next image in the series. Feel free to move back and forth as much as you like until you are comfortable with the story.
      </Typography>
      <Typography variant="h5" component='div' style={{marginTop: '1rem'}}>
        You will have up to 5 minutes to view the story, but you are not required to use the full 5 minutes.
      </Typography>
      <Typography variant="h5" component='div' style={{marginTop: '1rem'}}>
        Once the time is up, please retell the story in as much detail as possible.
      </Typography>
      <Typography variant="h5" component='div' style={{marginTop: '1rem'}}>
        When you are ready, please click the record button below.
      </Typography>
      <Typography variant="h5" component='div' style={{marginTop: '1rem'}}>
        Please note: Once you click the record button, the story will disappear.
      </Typography>
    </>,
    content: null,
    profileName: 'speech',
    recordButtonDisabled: true
  }]
};
export const StoryRecallDefinition = Definition;

export const StoryRecall =  ({onCompletion}: {onCompletion: () => void}) => {
  const { i18n } = useTranslation();
  const [task, setTask] = useState<AcousticTask>(() => ({
    ...Definition,
    id: randomUUID(),
    startedAt: new Date().toISOString(),
    completedAt: null,
    status: 'pending',
    subTasks: Definition.subTaskDefinitions.map((definition) => ({
      ...definition,
      id: randomUUID(),
      instructions: translateReactNode(definition.instructions, Definition.cohort),
      title: translateReactNode(definition.title, Definition.cohort) as string
    })),
    tutorialUrl: TUTORIAL_URLS[i18n.language] || TUTORIAL_URLS['en']
  }));

  const handleOnChange = () => {
    setTask(prev => {
      const updatedSubTask = prev.subTasks[0];
      updatedSubTask.recordButtonDisabled = false;
      return {...prev};
    });
  }

  useEffect(() => {
    setTask(prev => {
      const updatedSubTask = prev.subTasks[0];
      updatedSubTask.content = <div style={{ margin: '1rem 0' }}><StoryRecallTextDisplay onLastSlideShown={handleOnChange} cohort={task.cohort}/></div>;
      return {...prev};
    });
  }, []);

  const handleOnCompletion = () => {
    console.log("Audio task completed", task.title);
    onCompletion();
  }

  return (
    <div>
      <TaskView
        task={task}
        onCompletion={handleOnCompletion}
        />
    </div>
  );
}
